import { getSafeErrorMessage } from "../utils/errorMessages";
import {
  auth,
  signInWithEmailAndPassword as fbSignInWithEmail,
  GoogleAuthProvider,
  signInWithPopup as fbSignInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber as fbSignInWithPhoneNumber,
  fetchSignInMethodsForEmail as fbFetchSignInMethodsForEmail,
  linkWithCredential as fbLinkWithCredential,
  reload as fbReload,
} from "../firebaseConfig";
import {
  createUserWithEmailAndPassword as fbCreateUserWithEmail,
  updateProfile as fbUpdateProfile,
  PhoneAuthProvider,
  type PhoneAuthCredential,
  type ConfirmationResult,
  type User,
} from "firebase/auth";

/**
 * Centralized Firebase authentication service
 * Provides wrapped methods with consistent error handling
 */

export const authService = {
  /**
   * Sign in with email and password
   */
  signInWithEmail: async (email: string, password: string) => {
    try {
      const userCredential = await fbSignInWithEmail(auth, email, password);
      return { success: true, user: userCredential.user, error: null };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'signin-email') };
    }
  },

  /**
   * Create user with email and password
   */
  createUserWithEmail: async (email: string, password: string) => {
    try {
      const userCredential = await fbCreateUserWithEmail(auth, email, password);
      return { success: true, user: userCredential.user, error: null };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'signup-email') };
    }
  },

  /**
   * Update user profile (display name, photo URL)
   */
  updateProfile: async (user: User, updates: { displayName?: string; photoURL?: string }) => {
    try {
      await fbUpdateProfile(user, updates);
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: getSafeErrorMessage(error, 'update-profile') };
    }
  },

  /**
   * Sign in with Google
   */
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await fbSignInWithPopup(auth, provider);
      return { success: true, user: result.user, credential: null, error: null };
    } catch (error: any) {
      // Handle account-exists-with-different-credential error
      if (error.code === 'auth/account-exists-with-different-credential') {
        const email = error.customData?.email;
        const credential = GoogleAuthProvider.credentialFromError(error);
        return { 
          success: false, 
          user: null, 
          credential, 
          email,
          error: 'account-exists',
          needsLinking: true 
        };
      }
      return { success: false, user: null, credential: null, error: getSafeErrorMessage(error, 'signin-google') };
    }
  },

  /**
   * Fetch sign-in methods for email
   */
  fetchSignInMethodsForEmail: async (email: string): Promise<{ success: boolean; methods: string[]; error: string | null }> => {
    try {
      const methods = await fbFetchSignInMethodsForEmail(auth, email);
      return { success: true, methods, error: null };
    } catch (error: any) {
      return { success: false, methods: [], error: getSafeErrorMessage(error, 'fetch-methods') };
    }
  },

  /**
   * Link credential to current user
   */
  linkCredential: async (user: User, credential: any) => {
    try {
      const result = await fbLinkWithCredential(user, credential);
      return { success: true, user: result.user, error: null };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'link-credential') };
    }
  },

  /**
   * Send phone verification code
   */
  sendPhoneVerificationCode: async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
    try {
      const confirmationResult = await fbSignInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      return { success: true, confirmationResult, error: null };
    } catch (error: any) {
      return { success: false, confirmationResult: null, error: getSafeErrorMessage(error, 'send-phone-otp') };
    }
  },

  /**
   * Verify phone OTP
   */
  verifyPhoneOTP: async (confirmationResult: ConfirmationResult, code: string) => {
    try {
      const userCredential = await confirmationResult.confirm(code);
      return { success: true, user: userCredential.user, error: null };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'verify-phone-otp') };
    }
  },

  /**
   * Link phone credential to existing user
   */
  linkPhoneCredential: async (user: User, credential: PhoneAuthCredential) => {
    try {
      const result = await fbLinkWithCredential(user, credential);
      return { success: true, user: result.user, error: null };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'link-phone') };
    }
  },

  /**
   * Create phone credential from OTP verification
   */
  createPhoneCredential: (confirmationResult: ConfirmationResult, code: string) => {
    try {
      const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, code);
      return { success: true, credential, error: null };
    } catch (error: any) {
      return { success: false, credential: null, error: getSafeErrorMessage(error, 'create-phone-credential') };
    }
  },

  /**
   * Sign out
   */
  signOut: async () => {
    try {
      await auth.signOut();
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: getSafeErrorMessage(error, 'signout') };
    }
  },

  /**
   * Get current user
   */
  getCurrentUser: () => auth.currentUser,

  /**
   * Refresh current user's authentication data from Firebase
   */
  refreshUser: async () => {
    try {
      if (auth.currentUser) {
        await fbReload(auth.currentUser);
        return { success: true, user: auth.currentUser, error: null };
      }
      return { success: false, user: null, error: "No current user" };
    } catch (error: any) {
      return { success: false, user: null, error: getSafeErrorMessage(error, 'refresh-user') };
    }
  },

  /**
   * Complete login by establishing server session
   * Retries once with fresh token if initial attempt fails due to stale token
   */
  completeLogin: async (user: User, redirectUrl: string, retryCount: number = 0): Promise<{ success: boolean; redirectUrl: string | null; error: string | null }> => {
    try {
      const idToken = await user.getIdToken(true);
      const csrfToken = getCookie("csrfToken");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/sessionLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        // Retry once with fresh token if unauthorized
        if (response.status === 401 && retryCount === 0) {
          console.log('Token expired, retrying with fresh token...');
          return await authService.completeLogin(user, redirectUrl, 1);
        }
        throw new Error("Session login failed");
      }

      // Only sign out after successful session creation
      await auth.signOut();
      
      // Load user preferences (theme, language) after successful login
      // Import dynamically to avoid circular dependencies
      import('./settingsService').then(({ loadUserPreferences }) => {
        loadUserPreferences().catch(err => console.error('Failed to load preferences:', err));
      });
      
      return { success: true, redirectUrl, error: null };
    } catch (error: any) {
      return { success: false, redirectUrl: null, error: getSafeErrorMessage(error, 'complete-login') };
    }
  },
};

/**
 * Get CSRF token from cookies
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}
